import React, { useState } from 'react';
import { Pencil, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

export const PoNumberEditButton = ({ api, po, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [num, setNum] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const start = () => { setNum(po.po_number); setReason(''); setOpen(true); };
  const save = async () => {
    setBusy(true);
    try {
      await api().patch(`/purchase-orders/${po.id}/number`, { po_number: num, reason });
      toast.success(`PO number changed to ${num.trim()}`);
      setOpen(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change PO number'); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500" onClick={start} title="Change PO number (Admin)" data-testid="po-number-edit-btn"><Pencil className="w-3.5 h-3.5" /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="po-number-dialog">
          <DialogHeader><DialogTitle>Change PO Number</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>New PO number *</Label><Input value={num} onChange={(e) => setNum(e.target.value)} maxLength={40} className="rounded-sm mono" autoFocus data-testid="po-number-input" /></div>
            <div><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. typo, matches vendor invoice" className="rounded-sm" data-testid="po-number-reason" /></div>
            <p className="text-[11px] text-slate-500">Must be unique. The change is logged in the PO history. Already-sent copies keep the old number.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm" data-testid="po-number-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy || !num.trim()} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="po-number-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const VendorConfirmationBadge = ({ po }) => {
  const c = po?.vendor_confirmation;
  if (!c) {
    if (!(po?.emails || []).length) return null;
    return <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600" data-testid="po-vendor-awaiting"><Clock className="w-3 h-3" /> {po.unconfirmed_notified ? "Vendor hasn't confirmed" : "Awaiting vendor confirmation"}{(po.reminders || []).length ? ` · ${po.reminders.length} reminder${po.reminders.length > 1 ? 's' : ''} sent` : ''}</span>;
  }
  const ok = c.status === 'confirmed';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-sm font-semibold ${ok ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-testid="po-vendor-confirmation-badge"
      title={[c.vendor_reference && `Vendor ref ${c.vendor_reference}`, c.note, c.contact_name && `by ${c.contact_name}`, `on ${new Date(c.at).toLocaleString()}`].filter(Boolean).join(' · ')}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {ok ? `Vendor Confirmed · ships ${c.ship_date}` : `Vendor issue: ${c.problem_type}`}
      {c.vendor_reference && <span className="font-normal mono">· ref {c.vendor_reference}</span>}
    </span>
  );
};

export const VendorConfirmationNote = ({ po }) => {
  const c = po?.vendor_confirmation;
  if (!c || (!c.note && c.status === 'confirmed')) return null;
  return (
    <div className={`text-xs px-3 py-2 rounded-sm border ${c.status === 'confirmed' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`} data-testid="po-vendor-confirmation-note">
      <b>{c.contact_name || po.vendor_name_snapshot}</b> {c.status === 'confirmed' ? 'confirmed this PO' : `flagged: ${c.problem_type}`}{c.ship_date ? ` · ship date ${c.ship_date}` : ''} · {new Date(c.at).toLocaleString()}
      {c.note && <div className="mt-0.5 whitespace-pre-line">“{c.note}”</div>}
    </div>
  );
};
